import { DSButtonV2 } from '@elliemae/ds-button-v2';
import { Grid } from '@elliemae/ds-grid';
import type { ColorProps, LayoutProps, SizingProps, SpaceProps } from '@elliemae/ds-system';
import { styled, xStyledCommonProps } from '@elliemae/ds-system';
import { DSTypography } from '@elliemae/ds-typography';
import type {} from '@xstyled/system';
import type {} from '@xstyled/util';
import { BANNER_TYPES, DSBannerName, DSBannerSlots } from './exported-related/index.js';
import type { DSBannerT } from './react-desc-prop-types.js';

interface StyledBannerContainerT extends SizingProps, LayoutProps, SpaceProps, ColorProps {}
export const StyledBannerContainer = styled('div', {
  name: DSBannerName,
  slot: DSBannerSlots.CONTAINER,
})<StyledBannerContainerT>`
  min-height: 41px;
  overflow: hidden;
  transition: all 0.5s ease-in;
  @starting-style {
    max-height: 0px;
  }
  ${xStyledCommonProps}
`;

interface StyledInnerContainerT {}
export const StyledInnerContainer = styled(Grid, {
  name: DSBannerName,
  slot: DSBannerSlots.INNER_CONTAINER,
})<StyledInnerContainerT>`
  padding-right: 4px;
  padding-bottom: 6px;
  width: 100%;
  min-width: 0;
`;

const handleBorderColor = (
  type: DSBannerT.BannerTypesT,
  { colors }: { colors: Record<string, Record<string, string>> },
): string => {
  switch (type) {
    case BANNER_TYPES.SUCCESS:
      return colors.success[900];
    case BANNER_TYPES.INFO:
      return colors.brand[600];
    case BANNER_TYPES.WARNING:
      return colors.warning[900];
    case BANNER_TYPES.DANGER:
      return colors.danger[900];
    default:
      return 'should_not_happen';
  }
};
interface StyledBannerLayoutT {
  $type: DSBannerT.BannerTypesT;
}
export const StyledBannerLayout = styled('div', {
  name: DSBannerName,
  slot: DSBannerSlots.LAYOUT,
})<StyledBannerLayoutT>`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  background-color: neutral-000;
  border-bottom: 5px solid ${({ $type, theme }) => handleBorderColor($type, theme)};
  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`;

export const StyledIconContainer = styled('div', {
  name: DSBannerName,
  slot: DSBannerSlots.ICON_CONTAINER,
})`
  padding-top: 8px;
  margin-right: ${({ theme }) => theme.space.xxs};
  margin-left: ${({ theme }) => theme.space.xs};
  min-width: 0px;
`;

export const StyledTitle = styled(DSTypography, { name: DSBannerName, slot: DSBannerSlots.TITLE })`
  color: neutral-700;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  padding-top: 9px;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  min-width: 0px;
`;

export const StyledSubTitle = styled(DSTypography, { name: DSBannerName, slot: DSBannerSlots.SUBTITLE })`
  margin-left: ${({ theme }) => theme.space.xs};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  padding-top: 9px;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  min-width: 0px;
  ${({ theme }) => {
    if (['s', 'xs'].includes(theme.layoutMode!)) {
      return `
        grid-column: 1 / -1;
      `;
    }
    return '';
  }}
`;

interface StyledActionLinkT {
  isBodyEmpty: boolean;
}
export const StyledActionLink = styled('a', {
  name: DSBannerName,
  slot: DSBannerSlots.ACTION_LINK,
})<StyledActionLinkT>`
  display: inline-block;
  text-decoration: none;
  line-height: inherit;
+ font-size: inherit;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: brand-600;
  min-width: 0px;
  position: relative;
  &:focus {
    outline: none;
    &:after {
      position: absolute;
      content: '';
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      border-radius: 2px;
      border: 2px solid brand-700;
      box-shadow: 0 0 0 1px brand-700;
      pointer-events: none;
    }
  }
`;
export const StyledCloseButton = styled(DSButtonV2, {
  name: DSBannerName,
  slot: DSBannerSlots.CLOSE_BUTTON,
})`
  margin: 5px;
`;

export const StyledLiveRegionPortal = styled('div', {
  name: DSBannerName,
  slot: DSBannerSlots.LIVE_REGION_PORTAL,
})`
  clip: rect(0 0 0 0);
  position: absolute;
`;
